import { deleteAllStudiesToBurn } from 'actions/burnActions';
import axios from 'axios';
import { formatBytes } from 'components/dashboard/DashboardPage';
import { useAppDispatch } from 'hooks';
import React, { useEffect } from 'react';
import { Button } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import { BurnerJob, Printer } from 'reducers/printersReducer';
import { Reader } from 'reducers/readersReducer';
import { getBurnerFilters, getStudiesToBurn } from 'reducers/selectors';
import * as cdBurnfiltersActions from '../../../actions/burnerFiltersActions';
import { CDBurnerFilter } from 'reducers/burnerFiltersReducer';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTriangleExclamation } from '@fortawesome/free-solid-svg-icons';

type BurnStudiesButtonType = {
  selectedPrinter: Printer | null;
  selectedReader: Reader | null;
  selectedJob: BurnerJob | null;
  isReprinting: boolean;
  burnPriority: string|null;
};

const BurnStudiesButton = ({ selectedPrinter, selectedReader, selectedJob, isReprinting, burnPriority }: BurnStudiesButtonType) => {
  const dispatch = useAppDispatch();
  const filters : CDBurnerFilter[] = useSelector(getBurnerFilters);
  const studiesToBurn = useSelector(getStudiesToBurn);
  const toaster = {
    success: message => dispatch({ type: 'SUCCESS', message }),
    info: message => dispatch({ type: 'INFO', message }),
    warning: message => dispatch({ type: 'WARNING', message }),
    error: message => dispatch({ type: 'ERROR', message }),
  };

  useEffect(() => {
    dispatch(cdBurnfiltersActions.getFilters());
  }, [dispatch]);

  const burnDisk = (): void => {
    if (isReprinting) {
      if (selectedJob !== null) {
        sendBurnRequest(selectedJob.studies);
        document.body.click(); // close overlay
      } else {
        toaster.error(`No selected Job`);
      }
    } else {
      sendBurnRequest(studiesToBurn);
      dispatch(deleteAllStudiesToBurn());
    }
  };

  const sendBurnRequest = (studies): void => {
    let priority = burnPriority;
    if (priority === "-") {
      priority = null;
    }
    const data = {
      printer: selectedPrinter!.name,
      reader: selectedReader!.displayName,
      studies: studies,
      priority: priority,
    };
    toaster.info(`Sending print request`);

    axios
      .post(`/rest/react/cdburner`, data, {
        headers: {
          'Content-type': 'application/json',
        },
      })
      .then(() => toaster.success(`Print request sent correctly`))
      .catch(e => toaster.error(e.message));
  };

  const showEstimatedSize = (): boolean => {
    if (isReprinting) {
      if (!doAllStudiesStillExist() || selectedReader === null) {
        return false;
      }
    } else {
      if (!doAllStudiesHaveSize() || selectedReader === null) {
        return false;
      }
    }
    return true;
  };

  const doAllStudiesHaveSize = (): boolean => {
    return studiesToBurn.every(study => study.size);
  };

  const getAllStudiesSize = (): number => {
    let numberBytes = 0;
    if (isReprinting) {
      if (selectedJob !== null) {
        selectedJob.studies.forEach(study => {
          if (study.size) {
            numberBytes += parseFloat(study.size);
          }
        });
      }
    } else {
      studiesToBurn.forEach(element => {
        if (element.size) {
          numberBytes += parseFloat(element.size);
        }
      });
    }
    return numberBytes;
  };

  const doAllStudiesStillExist = (): boolean => {
    return selectedJob!.studies.every(study => study.description !== 'STUDY NOT FOUND');
  };

  const disablePrintButton = (): boolean => {

    if (isReprinting && selectedJob === null) {
      return true;
    }

    if (isReprinting && selectedJob !== null && !doAllStudiesStillExist()) {
      return true;
    }

    return false;
  };

  return (
    <div>
      <div style={{ paddingBottom: '5px' }}>
        <div style={{ fontSize: '15px', width: '20%', textAlign: 'left' }} className="divTableBurnOverlay">
          Estimated size
        </div>
        <div style={{ width: '35%', paddingLeft: '5px' }} className="divTableBurnOverlay">
          {showEstimatedSize() ? (
            <div
              style={{
                fontSize: '15px',
              }}
            >
              {formatBytes(getAllStudiesSize() + selectedReader!.size)}
            </div>
          ) : (
            <div>Calculating...</div>
          )}
        </div>
      </div>
      {filters && filters.find(filter => filter.enabled) && (
        <div style={{ paddingBottom: '5px' }}>
          <div className="divTableBurnOverlay alertBurnOverlay">
            <FontAwesomeIcon icon={faTriangleExclamation} className="warn-info-icon" style={{ cursor: 'default' }} />
            <p className="alertBurnHint">Filters are active, some instances may not get printed in the support</p>
          </div>
        </div>
      )}
      <div>
        {selectedPrinter !== null && selectedReader !== null && (
          <div>
            <div id="print-button-container">
              <Button
                disabled={disablePrintButton()}
                variant="success"
                size="sm"
                onClick={() => {
                  burnDisk();
                }}
              >
                Print CD
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BurnStudiesButton;
